import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectVisibleRisks, updateRiskPosition, addRisk, deleteRisk, addRiskItem, updateRiskItem, updateRisk, deleteRiskItem, setRisks } from './riskRegisterSlice';
import './riskRegister.css';
import { RiskMatrix } from './RiskMatrix';

// Import templates
import TechnicalRisks from './templates/TechnicalRisks.json';
import ProjectRisks from './templates/ProjectRisks.json';
import BusinessRisks from './templates/BusinessRisks.json';
import ComprehensiveRisks from './templates/ComprehensiveRisks.json';

export const RiskRegister = () => {
  const dispatch = useDispatch();
  const risks = useSelector(selectVisibleRisks);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);

  const templates = [
    { id: 'technical', name: 'Technical Risks', data: TechnicalRisks },
    { id: 'project', name: 'Project Risks', data: ProjectRisks },
    { id: 'business', name: 'Business Risks', data: BusinessRisks },
    { id: 'comprehensive', name: 'Comprehensive Risk Assessment', data: ComprehensiveRisks }
  ];

  const handleAddRisk = () => {
    dispatch(addRisk({
      title: 'New Risk',
      position: { x: 100, y: 100 }
    }));
  };

  const loadTemplate = (template) => {
    const convertedRisks = template.data.risks.map((risk, index) => ({
      id: `risk-${Date.now()}-${index}`,
      title: risk.title,
      position: { x: 100, y: 100 },
      onScreen: true,
      items: risk.items.map((item, itemIndex) => ({
        id: `item-${Date.now()}-${index}-${itemIndex}`,
        title: item.title,
        impact: item.impact,
        probability: item.probability
      }))
    }));

    dispatch(setRisks(convertedRisks));
    setShowTemplateModal(false);
  };

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', minHeight: '600px' }}>
      <div style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <h2 style={{ margin: 0 }}>Risk Register</h2>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setShowTemplateModal(true)}
              style={{
                padding: '8px 12px',
                backgroundColor: '#17a2b8',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '14px'
              }}
              title="Templates"
            >
              📋 Templates
            </button>
            <button
              onClick={() => setShowHelpModal(true)}
              style={{
                padding: '8px 12px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '16px',
                width: '36px',
                height: '36px'
              }}
              title="Help"
            >
              ❓
            </button>
          </div>
        </div>
        <button
          onClick={handleAddRisk}
          style={{
            padding: '8px 16px',
            backgroundColor: '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 'bold'
          }}
        >
          + Add Risk
        </button>
      </div>

      {/* Help Modal */}
      {showHelpModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowHelpModal(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '600px',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, color: '#dc3545' }}>Risk Register Help</h2>
              <button
                onClick={() => setShowHelpModal(false)}
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Close
              </button>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ color: '#333', marginBottom: '10px' }}>How to Use</h3>
              <ul style={{ lineHeight: '1.8' }}>
                <li><strong>Add Risk:</strong> Click "+ Add Risk" to create a new risk card</li>
                <li><strong>Move Risks:</strong> Click and drag any risk card to reposition it</li>
                <li><strong>Edit Titles:</strong> Double-click on any title to edit it (risk or risk item)</li>
                <li><strong>Add Risk Items:</strong> Click "+ Add Risk Item" within a risk card to add detailed items</li>
                <li><strong>Delete:</strong> Click the "Delete" button to remove a risk</li>
                <li><strong>Impact & Probability:</strong> Each risk item shows impact and probability values</li>
                <li><strong>Risk Score:</strong> Automatically calculated as Impact × Probability</li>
              </ul>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ color: '#333', marginBottom: '10px' }}>Risk Examples</h3>
              <div style={{ backgroundColor: '#f9f9f9', padding: '15px', borderRadius: '8px', marginBottom: '10px' }}>
                <strong style={{ color: '#dc3545' }}>Technical Risks</strong>
                <ul style={{ marginTop: '8px', lineHeight: '1.6' }}>
                  <li>Technology becomes obsolete</li>
                  <li>Third-party API changes or deprecates</li>
                  <li>Data migration failures</li>
                  <li>Security vulnerabilities discovered</li>
                </ul>
              </div>

              <div style={{ backgroundColor: '#f9f9f9', padding: '15px', borderRadius: '8px', marginBottom: '10px' }}>
                <strong style={{ color: '#dc3545' }}>Project Risks</strong>
                <ul style={{ marginTop: '8px', lineHeight: '1.6' }}>
                  <li>Scope creep beyond initial requirements</li>
                  <li>Key team member leaves project</li>
                  <li>Budget overruns or funding cuts</li>
                  <li>Timeline delays due to dependencies</li>
                </ul>
              </div>

              <div style={{ backgroundColor: '#f9f9f9', padding: '15px', borderRadius: '8px' }}>
                <strong style={{ color: '#dc3545' }}>Business Risks</strong>
                <ul style={{ marginTop: '8px', lineHeight: '1.6' }}>
                  <li>Market conditions change unexpectedly</li>
                  <li>Competitor releases similar product first</li>
                  <li>Regulatory compliance requirements change</li>
                  <li>Customer adoption lower than expected</li>
                </ul>
              </div>
            </div>

            <div style={{ padding: '15px', backgroundColor: '#fff3cd', borderRadius: '8px', border: '1px solid #ffc107' }}>
              <strong>💡 Pro Tip:</strong> Assign impact (1-5) and probability (1-5) to each risk item. Risks with scores above 15 should be prioritized for mitigation planning.
            </div>

            <div style={{ marginTop: '15px', padding: '15px', backgroundColor: '#e7f3ff', borderRadius: '8px', border: '1px solid #17a2b8' }}>
              <strong>📋 Templates:</strong> Use the Templates button to load pre-built risk registers. The Comprehensive Risk Assessment template includes all risk categories for a complete project analysis.
            </div>
          </div>
        </div>
      )}

      {/* Template Modal */}
      {showTemplateModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowTemplateModal(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '500px',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, color: '#dc3545' }}>Risk Register Templates</h2>
              <button
                onClick={() => setShowTemplateModal(false)}
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Close
              </button>
            </div>

            <p style={{ marginBottom: '20px', color: '#666' }}>
              Choose a template to start with pre-populated risk categories and examples:
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {templates.map((template) => (
                <button
                  key={template.id}
                  onClick={() => loadTemplate(template)}
                  style={{
                    padding: '15px',
                    backgroundColor: '#f8f9fa',
                    border: '2px solid #dc3545',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    color: '#333',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#dc3545';
                    e.target.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#f8f9fa';
                    e.target.style.color = '#333';
                  }}
                >
                  {template.name}
                </button>
              ))}
            </div>

            <div style={{ marginTop: '20px', padding: '12px', backgroundColor: '#fff3cd', borderRadius: '8px', border: '1px solid #ffc107', fontSize: '14px' }}>
              <strong>⚠️ Note:</strong> Loading a template will replace your current risk register data.
            </div>
          </div>
        </div>
      )}

      {/* Risk Matrix Chart */}
      <RiskMatrix />

      {/* Risk Register Table */}
      <div style={{ backgroundColor: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginTop: '20px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#dc3545', color: 'white' }}>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #c82333' }}>Title</th>
              <th style={{ padding: '12px', textAlign: 'center', width: '120px', borderBottom: '2px solid #c82333' }}>Impact</th>
              <th style={{ padding: '12px', textAlign: 'center', width: '120px', borderBottom: '2px solid #c82333' }}>Probability</th>
              <th style={{ padding: '12px', textAlign: 'center', width: '100px', borderBottom: '2px solid #c82333' }}>Score</th>
              <th style={{ padding: '12px', textAlign: 'center', width: '100px', borderBottom: '2px solid #c82333' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {risks.map((risk) => (
              <RiskRegisterItem key={risk.id} risk={risk} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const RiskRegisterItem = ({ risk }) => {
  const dispatch = useDispatch();
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState(risk.title);
  const [editingItemId, setEditingItemId] = useState(null);
  const [editedItemTitles, setEditedItemTitles] = useState({});
  const [editingImpact, setEditingImpact] = useState(null);
  const [editingProbability, setEditingProbability] = useState(null);

  const handleAddItem = () => {
    dispatch(addRiskItem({
      riskId: risk.id,
      item: { title: 'New Risk Item', impact: 1, probability: 1 }
    }));
  };

  const handleDeleteRisk = () => {
    dispatch(deleteRisk(risk.id));
  };

  const handleTitleDoubleClick = () => {
    setIsEditingTitle(true);
    setEditedTitle(risk.title);
  };

  const handleTitleChange = (e) => {
    setEditedTitle(e.target.value);
  };

  const handleTitleBlur = () => {
    if (editedTitle.trim()) {
      dispatch(updateRisk({ id: risk.id, updates: { title: editedTitle } }));
    }
    setIsEditingTitle(false);
  };

  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleTitleBlur();
    } else if (e.key === 'Escape') {
      setEditedTitle(risk.title);
      setIsEditingTitle(false);
    }
  };

  const handleItemTitleDoubleClick = (itemId, currentTitle) => {
    setEditingItemId(itemId);
    setEditedItemTitles({ ...editedItemTitles, [itemId]: currentTitle });
  };

  const handleItemTitleChange = (itemId, value) => {
    setEditedItemTitles({ ...editedItemTitles, [itemId]: value });
  };

  const handleItemTitleBlur = (itemId) => {
    const newTitle = editedItemTitles[itemId];
    if (newTitle && newTitle.trim()) {
      dispatch(updateRiskItem({ riskId: risk.id, itemId, updates: { title: newTitle } }));
    }
    setEditingItemId(null);
  };

  const handleItemTitleKeyDown = (e, itemId) => {
    if (e.key === 'Enter') {
      handleItemTitleBlur(itemId);
    } else if (e.key === 'Escape') {
      setEditingItemId(null);
    }
  };

  const handleImpactChange = (itemId, value) => {
    const numValue = parseInt(value) || 1;
    if (numValue >= 1 && numValue <= 5) {
      dispatch(updateRiskItem({ riskId: risk.id, itemId, updates: { impact: numValue } }));
    }
  };

  const handleProbabilityChange = (itemId, value) => {
    const numValue = parseInt(value) || 1;
    if (numValue >= 1 && numValue <= 5) {
      dispatch(updateRiskItem({ riskId: risk.id, itemId, updates: { probability: numValue } }));
    }
  };

  const handleDeleteItem = (itemId) => {
    dispatch(deleteRiskItem({ riskId: risk.id, itemId }));
  };

  return (
    <>
      {/* Risk Category Row */}
      <tr style={{ backgroundColor: '#f8d7da', borderTop: '2px solid #dc3545' }}>
        <td colSpan="5" style={{ padding: '10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {isEditingTitle ? (
              <input
                type="text"
                value={editedTitle}
                onChange={handleTitleChange}
                onBlur={handleTitleBlur}
                onKeyDown={handleTitleKeyDown}
                autoFocus
                style={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  border: '2px solid #dc3545',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  flex: 1,
                  marginRight: '10px'
                }}
              />
            ) : (
              <strong
                onDoubleClick={handleTitleDoubleClick}
                style={{ cursor: 'text', flex: 1, fontSize: '16px' }}
                title="Double-click to edit"
              >
                {risk.title}
              </strong>
            )}
            <div>
              <button
                onClick={handleAddItem}
                style={{
                  padding: '4px 12px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  marginRight: '8px'
                }}
              >
                + Add Item
              </button>
              <button
                onClick={handleDeleteRisk}
                style={{
                  padding: '4px 12px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </td>
      </tr>
      {/* Risk Items Rows */}
      {risk.items && risk.items.map((item, index) => (
        <tr key={item.id} style={{ backgroundColor: index % 2 === 0 ? 'white' : '#f9f9f9', borderBottom: '1px solid #dee2e6' }}>
          <td style={{ padding: '12px' }}>
            {editingItemId === item.id ? (
              <input
                type="text"
                value={editedItemTitles[item.id] || item.title}
                onChange={(e) => handleItemTitleChange(item.id, e.target.value)}
                onBlur={() => handleItemTitleBlur(item.id)}
                onKeyDown={(e) => handleItemTitleKeyDown(e, item.id)}
                autoFocus
                style={{
                  border: '1px solid #dc3545',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  fontSize: '14px',
                  width: '100%'
                }}
              />
            ) : (
              <span
                onDoubleClick={() => handleItemTitleDoubleClick(item.id, item.title)}
                style={{ cursor: 'text' }}
                title="Double-click to edit"
              >
                {item.title}
              </span>
            )}
          </td>
          <td style={{ padding: '12px', textAlign: 'center' }}>
            <input
              type="number"
              min="1"
              max="5"
              value={item.impact}
              onChange={(e) => handleImpactChange(item.id, e.target.value)}
              style={{
                width: '60px',
                padding: '4px',
                textAlign: 'center',
                border: '1px solid #ced4da',
                borderRadius: '4px'
              }}
            />
          </td>
          <td style={{ padding: '12px', textAlign: 'center' }}>
            <input
              type="number"
              min="1"
              max="5"
              value={item.probability}
              onChange={(e) => handleProbabilityChange(item.id, e.target.value)}
              style={{
                width: '60px',
                padding: '4px',
                textAlign: 'center',
                border: '1px solid #ced4da',
                borderRadius: '4px'
              }}
            />
          </td>
          <td style={{ padding: '12px', textAlign: 'center', fontWeight: 'bold', color: '#dc3545', fontSize: '16px' }}>
            {item.impact * item.probability}
          </td>
          <td style={{ padding: '12px', textAlign: 'center' }}>
            <button
              onClick={() => handleDeleteItem(item.id)}
              style={{
                padding: '4px 8px',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '11px'
              }}
            >
              Delete
            </button>
          </td>
        </tr>
      ))}
    </>
  );
};